import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ColDef, ColumnResizedEvent, GridOptions, GridReadyEvent, GridSizeChangedEvent, RowClickedEvent } from 'ag-grid-community';
import { CaseService } from '@app/core/services/case.service';
import { AggWidgetHeaderComponent } from '@app/shared/components/agg-widget-header/agg-widget-header.component';
import { ResponseColumn, ResponseRow, SearchResponse } from '@app/shared/models/response';
import { CaseOverviewMetaData } from '@app/shared/models/case-overview-meta-data';
import { SearchParams, SearchSuggestionList } from '@app/shared/models/search';
import { FilterGroup } from '@app/shared/models/filter';
import { Case, CasesFrom, CaseStatus } from '@app/shared/models/case';
import { BaseWithBundleComponent } from '@app/shared/components/base/base-with-bundle.component';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { ConfirmModalComponent } from '@app/shared/dialogs/confirm-dialog/confirm-modal.component';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { OP } from '@app/core/services/base/base-service-with-bundle.service';
import { Permission } from '@app/core/authentication/permission';
import { CaseModuleRoutes } from '@app/modules/case/case-routes';
import { WidgetService } from '@app/core/services/widget.service';
import { Widget, WidgetSettings } from '@app/shared/models/widget';
import { CaseWidgetSettingsDialogComponent, CaseWidgetSettingsDialogResult } from '../case-widget-settings-dialog/case-widget-settings-dialog.component';
import { DrI18nService } from '@app/core/services/i18n/i18n.service';
import { AgGridAngular } from 'ag-grid-angular';
import { InboxService } from '@app/core/services/inbox.service';
import { InboxWebSocketService } from '@app/core/services/web-socket/inbox-web-socket.service';
import { InboxActionCellRendererComponent } from './inbox-action-cell-renderer.component';
import { Inbox } from '@app/shared/models/inbox';
import {TextAreaDialogComponent} from "@app/shared/dialogs/text-area-dialog/text-area-dialog.component";
import { DrLocalStorageService } from '@app/core/services/local-storage/dr-local-storage.service';
import { BehaviorSubject } from 'rxjs';


@Component({
  selector: 'app-case-widget',
  templateUrl: './case-widget.component.html',
  styleUrls: ['./case-widget.component.scss'],
  providers: [CaseService]
})
export class CaseWidgetComponent extends BaseWithBundleComponent implements OnInit, OnDestroy {
  @ViewChild('agGridCaseWidget', { static: true }) agGrid: AgGridAngular;
  @ViewChild('agGridInbox', { static: false }) agGridInbox: AgGridAngular;

  private contentStoreRepositoryModel = 'contentstore_repository_model';
  
  private overviewWidget: Widget;

  public selectAll = false;
  public casesResult: SearchResponse;
  public metaDataResult: CaseOverviewMetaData;
  public rowData: any[];
  public editMode = false;
  public inboxEditMode = false;
  public inboxSelectAll = false;
  public gridOptions: GridOptions;
  public inboxGridOptions: GridOptions;
  public searchCriteria: SearchParams;
  public statusFilterOptions: FilterGroup;
  public statusFilterSelected: CaseStatus;
  public suggestionList: SearchSuggestionList;

  public inboxEntryCount: number = 0;

  private timers: number[] = [];
  private readonly DEFAULT_COL_WIDTH = 200; // this is the default width when the property is not set
  private overviewRefresh$ = new BehaviorSubject(false);

  private headerComponentParams = {
    agMatIconName: 'create',
    callbackFn: () => {
      this.toggleEditMode();
    }
  };

  private inboxHeaderComponentParams = {
    agMatIconName: 'create',
    callbackFn: () => {
      this.toggleInboxEditMode();
    }
  };

  protected readonly caseColumns = {
    case_no: marker('case_widget.column.case_no'),
    claim_date: marker('case_widget.column.claim_date'),
    claim_date_formatted: marker('case_widget.column.claim_date_formatted'),
    claim_type: marker('case_widget.column.claim_type'),
    client_id: marker('case_widget.column.client_id'),
    creation_date: marker('case_widget.column.creation_date'),
    office: marker('case_widget.column.office'),
    participant_reference_numbers: marker('case_widget.column.participant_reference_numbers'),
    vehicle_kba_manufacturer: marker('case_widget.column.vehicle_kba_manufacturer'),
    vehicle_kba_type: marker('case_widget.column.vehicle_kba_type'),
    vehicle_manufacturer: marker('case_widget.column.vehicle_manufacturer'),
    vehicle_model: marker('case_widget.column.vehicle_model'),
    vehicle_plate_number: marker('case_widget.column.vehicle_plate_number'),
    vehicle_submodel: marker('case_widget.column.vehicle_submodel'),
    vehicle_type: marker('case_widget.column.vehicle_type'),
    vehicle_vin: marker('case_widget.column.vehicle_vin')
  };

  protected readonly inboxColumns = {
    order_number: marker('inbox_widget.column.order_number'),
    order_state: marker('inbox_widget.column.order_state'),
    creation_date: marker('case_widget.column.creation_date'),
    claim_date: marker('inbox_widget.column.claim_date'),
    manufacturer: marker('inbox_widget.column.manufacturer'),
    model: marker('inbox_widget.column.model'),
    plate_number: marker('inbox_widget.column.plate_number'),
    vin: marker('inbox_widget.column.vin'),
    participant_vo_name1: marker('inbox_widget.column.participant_vo_name1'),
    participant_vo_name2: marker('inbox_widget.column.participant_vo_name2'),
    participant_vo_name3: marker('inbox_widget.column.participant_vo_name3'),
    participant_cu_name1: marker('inbox_widget.column.participant_cu_name1'),
    participant_cu_name2: marker('inbox_widget.column.participant_cu_name2'),
    participant_cu_name3: marker('inbox_widget.column.participant_cu_name3'),
  };

  public constructor(
    protected componentServiceBundle: ComponentServiceBundle,
    protected caseService: CaseService,
    protected widgetService: WidgetService,
    protected inboxService: InboxService,
    protected localStorageService: DrLocalStorageService,
    protected inboxWebSocketService: InboxWebSocketService) {
    super(componentServiceBundle);
    this._idPrefix = 'case_widget.';
  }

  public ngOnInit() {
    super.ngOnInit();
    this.statusFilterSelected = CaseStatus.ALL;

    this.gridOptions = this.getGridOptions();
    this.inboxGridOptions = this.getGridOptions(false);
    this.inboxGridOptions.context = { componentParent: this };
    this.inboxGridOptions.frameworkComponents = {
      ...this.inboxGridOptions.frameworkComponents,
      inboxActionCellRenderer: InboxActionCellRendererComponent
    };

    const inboxSub = this.inboxWebSocketService.inboxSockets
      .subscribe(() => this.getInboxData());
    this.subscriptions.push(inboxSub);

    this.widgetService.getWidgetList().subscribe((result: Widget[]) => {
      let overviewWidget = result.find(o => o.name == 'smp.case.overview');
      if (overviewWidget) {
        this.overviewWidget = overviewWidget;
      
        this.initOverviewWidget(overviewWidget.settings);
      }
    }, 
      err => this.handleError(err)
    );

    this.caseService.getCasesOverviewMeta()
                    .subscribe((result: CaseOverviewMetaData) => {
        this.metaDataResult = result;
        this.statusFilterOptions = this.metaDataResult.getFilterGroupById('STATUS');
      },
        err => this.handleError(err)
      );
  }

  public ngOnDestroy(): void {
    this.timers.forEach(timerId => clearTimeout(timerId));
    this.timers = [];
  }

  private get inboxColumnsState(): ColDef[] | null {
    return this.localStorageService.get(this.localStorageService.STORAGE_KEY_INBOX_COLUMN_STATE);
  }

  private get caseColumnsState(): ColDef[] | null {
    return this.localStorageService.get(this.localStorageService.STORAGE_KEY_CASE_COLUMN_STATE);
  }

  public getCaseOverviewData(init?: boolean): void {
    this.caseService.getCasesOverview(this.searchCriteria)
                    .subscribe((result: SearchResponse) => {
        this.casesResult = result;

        if (init) {
          const columnDefs = this.formatResultColumns();
          this.agGrid.api.setColumnDefs([]); // colId = column.field, but it gets appended with {{index}}, so first set empty colum array
          this.agGrid.api.setColumnDefs(columnDefs);
        }

        const timerId = setTimeout(() => {
          this.formatResultRows();
          this.agGrid.api.setRowData(this.rowData);
        });

        this.timers.push(timerId);
      },
        err => this.handleError(err)
      );
  }

  public getInboxData(): void {
    this.inboxEntryCount = 0;

    if (!this.authenticationService.currentUserValue.hasPermissions(Permission.INBOX_WIDGET)) {
      return;
    }

    this.inboxService.getInboxOverviewData().subscribe((result) => {
      const columnDefs = this.formattedInboxColumns;

      this.agGridInbox.api.setColumnDefs([]);
      this.agGridInbox.api.setColumnDefs(columnDefs);

      this.inboxEntryCount = result.items.length;

      const timerId = setTimeout(() => {
        this.agGridInbox.api.setRowData(result.items);

        if (this.overviewRefresh$.value) {
          this.getCaseOverviewData(true);
          this.overviewRefresh$.next(false);
        }
      });

      this.timers.push(timerId);
    });
  }

  private getColumnWidthFromLS(colId: string, isInbox?: boolean): number {
    let value: number | undefined;

    if (isInbox) {
      value = this.inboxColumnsState?.find(v => v.colId === colId)?.width;
    } else {
      value = this.caseColumnsState?.find(v => v.colId === colId)?.width;
    }

     return value || this.DEFAULT_COL_WIDTH;
  }

  public getSuggestions(suggestionInput): void {
    if (suggestionInput) {
      this.caseService.getCasesOverviewSuggestions(suggestionInput)
                      .subscribe((suggestionList) => {
        const columnTransKeyPrefix = 'case_widget.column.';

        suggestionList.suggestions.forEach(searchTerm => {
          searchTerm.suggestionTransKey = `${columnTransKeyPrefix}${searchTerm.suggestion}`;
        });

        this.suggestionList = suggestionList;
      });
    } else {
      this.suggestionList = new SearchSuggestionList();
    }
  }

  // case overview columns

  private formatResultColumns(): ResponseColumn[] {
    let columnDefs = new Array<ResponseColumn>();

    if (this.authenticationService.currentUserValue.hasPermissions(Permission.SMP_CASE_DELETE)) {
      const checkBoxColumn = new ResponseColumn();
      checkBoxColumn.minWidth = 50;
      checkBoxColumn.maxWidth = 55;
      checkBoxColumn.headerName = 'selected';
      checkBoxColumn.field = 'selected';
      checkBoxColumn.checkboxSelection = this.editMode;
      checkBoxColumn.sortable = false;
      checkBoxColumn.cellRenderer = (params: any) => this.cellRenderer(params);
      checkBoxColumn.headerComponentParams = this.headerComponentParams;
      checkBoxColumn.cellClass = 'dr-grid-class';
      checkBoxColumn.headerClass = 'dr-grid-header-class';
      columnDefs.push(checkBoxColumn);
    }

    for (let i = 0; i < this.casesResult.columns.length; i++) {
      if (this.casesResult.columns[i].id !== this.contentStoreRepositoryModel) {
        const formattedColumn = this.casesResult.columns[i];
        formattedColumn.headerName = this.caseColumns[formattedColumn.id] ? this.translateService.instant(this.caseColumns[formattedColumn.id]) : formattedColumn.id;
        formattedColumn.field = formattedColumn.id;
        formattedColumn.cellRenderer = (params: any) => this.cellRenderer(params);
        formattedColumn.cellClass = 'dr-grid-class';
        formattedColumn.headerClass = 'dr-grid-header-class';
        formattedColumn.resizable = true;
        formattedColumn.width = this.getColumnWidthFromLS(formattedColumn.id);
        columnDefs.push(formattedColumn);
      }
    }

    return columnDefs;
  }

  // inbox columns

  private formatColumns(columns: ResponseColumn[]): ResponseColumn[] {
    const columnDefs = new Array<ResponseColumn>();

    if (this.authenticationService.currentUserValue.hasPermissions(Permission.INBOX_DELETE)) {
      const checkBoxColumn = new ResponseColumn();
      checkBoxColumn.minWidth = 50;
      checkBoxColumn.maxWidth = 55;
      checkBoxColumn.headerName = 'selected';
      checkBoxColumn.field = 'selected';
      checkBoxColumn.checkboxSelection = this.inboxEditMode;
      checkBoxColumn.sortable = false;
      checkBoxColumn.headerComponentParams = this.inboxHeaderComponentParams;
      checkBoxColumn.cellClass = 'dr-grid-class';
      checkBoxColumn.headerClass = 'dr-grid-header-class';
      columnDefs.push(checkBoxColumn);
    }

    for (let i = 0; i < columns.length; i++) {
      const formattedColumn = columns[i];
      formattedColumn.headerName = this.inboxColumns[formattedColumn.id] ? this.translateService.instant(this.inboxColumns[formattedColumn.id]) : formattedColumn.id;
      formattedColumn.field = formattedColumn.id;
      formattedColumn.cellRenderer = (params: any) => this.cellRenderer(params);
      formattedColumn.cellClass = 'dr-grid-class';
      formattedColumn.headerClass = 'dr-grid-header-class';
      formattedColumn.resizable = true;
      formattedColumn.width = this.getColumnWidthFromLS(formattedColumn.id, true);
      columnDefs.push(formattedColumn);
    }

    const actionsColumn = new ResponseColumn();
    actionsColumn.minWidth = 100;
    actionsColumn.maxWidth = 110;
    actionsColumn.headerName = '-';
    actionsColumn.field = '_inbox_actions';
    actionsColumn.sortable = false;
    actionsColumn.cellRenderer = 'inboxActionCellRenderer';
    actionsColumn.cellClass = 'dr-grid-class';
    actionsColumn.headerClass = 'dr-grid-header-class';
    columnDefs.push(actionsColumn);


    return columnDefs;
  }

  // search results rows

  private formatResultRows(): void {
    const contentStoreRepoModelIdx = this.searchCriteria.columns.split(',').indexOf(this.contentStoreRepositoryModel);
    this.rowData = new Array<any>();

    for (let i = 0; i < this.casesResult.rows.length; i++) {
      const formattedRow = this.casesResult.rows[i];

      if (contentStoreRepoModelIdx > -1) {
        const contentStoreRepositoryModelValue = formattedRow.values.splice(contentStoreRepoModelIdx, 1);
        formattedRow[this.contentStoreRepositoryModel] = contentStoreRepositoryModelValue[0];
      }

      for (let y = 0; y < this.casesResult.columns.length; y++) {
        if (y < formattedRow.values.length) {
          formattedRow[this.casesResult.columns[y].id] = formattedRow.values[y];
        }
      }

      this.rowData.push(formattedRow);
    }
  }

  private onRowClickHandler(rowClickedEvent: RowClickedEvent): void {
    if (!this.editMode) {
      const caseId = rowClickedEvent && rowClickedEvent.data && rowClickedEvent.data.id;
      const contentStoreRepositoryModel = rowClickedEvent && rowClickedEvent.data && rowClickedEvent.data[this.contentStoreRepositoryModel];

      this.caseService.validateCaseContentStoreModel(caseId, contentStoreRepositoryModel)
                      .subscribe(() => {
        this.routeToCase(caseId);
      });
    }
  }

  private cellRenderer(params: any): any {
    // Only displaying test photo for the moment
    if (params.column.colId === 'vehicle_foto' &&  params.value) {
      const img = 'assets/images/car-test.jpg';
      return '<mat-card>' +
                '<img mat-card-image src="' + img + '" alt="' + img + '" style="height: 100%;">' +
             '</mat-card>';
    }

    if (params.colDef.type === 'class java.util.Date') {
      return DrI18nService.dateTimeString(params.value);
    }

    if (params.column.colId === 'claim_date_formatted') {
        return params.value ? DrI18nService.dateString(params.value) : '';
    }

    if (params.colDef.type === 'class java.lang.String' && params.value && params.value[0] === '[' && params.value[(params.value.length - 1)] === ']') {
      return params.value.substring(1, (params.value.length - 1));
    }

    // default:
    return params.value;
  }

  private getSelectedRows(): any {
    return this.agGrid.api.getSelectedRows();
  }

  private routeToCase(caseId: string): void {
    const quickProcessingRoute = CaseModuleRoutes.QUICK_PROCESSING.replace(':id', caseId);
    this.router.navigateByUrl(quickProcessingRoute)
               .then();
  }

  private getGridOptions(hasRowClickHandler: boolean = true): GridOptions {
    return {
      debug                       : false,
      headerHeight                : 40,
      suppressMovableColumns      : true,
      suppressNoRowsOverlay       : true,
      suppressCellSelection       : true,
      suppressRowClickSelection   : true,
      rowSelection                : 'multiple',
      rowClass                    : 'dr-row-class mat-typography',
      domLayout                   : 'autoHeight',
      frameworkComponents         : { agColumnHeader: AggWidgetHeaderComponent },
      onRowClicked                : (event: RowClickedEvent) => hasRowClickHandler && this.onRowClickHandler(event),
      getRowNodeId                : (row: ResponseRow) => {
        let idx = row.id; //this.rowData.indexOf(row);
        return this.idPrefix + 'overview.row_' + idx;
      },
      columnTypes : {
        'class java.lang.String'  : {},
        'class java.util.Date'    : {}
      },
    };
  }

  public toggleSelectAllCases(): void {
    this.selectAll = !this.selectAll;

    if (this.selectAll) {
      this.agGrid.api.selectAll();
    } else {
      this.agGrid.api.deselectAll();
    }
  }

  public toggleSelectAllInboxEntries(): void {
    this.inboxSelectAll = !this.inboxSelectAll;

    if (this.inboxSelectAll) {
      this.agGridInbox.api.selectAll();
    } else {
      this.agGridInbox.api.deselectAll();
    }
  }

  public newCase(): void {
    const newCase = new Case();

    const subscription = this.caseService.openCaseCreationDialog(newCase, this.currentUser)
                                         .subscribe(dialogResult => {
      if (dialogResult) {
        newCase.office_id = dialogResult.office && dialogResult.office.id || '';
        newCase.office = dialogResult.office && dialogResult.office.officeCode || '';
        newCase.workflow = dialogResult.workflow || null;

        this.caseService.createByWorkflow(newCase)
            .subscribe((result) => {
              this.routeToCase(result.id);
            },
            err => this.handleError(err)
        );
      }
    });

    this.subscriptions.push(subscription);
  }

  get hasInboxPermission(): boolean {
    return this.authenticationService.currentUserValue.hasPermissions(Permission.INBOX_WIDGET);
  }

  get hasInboxDeletePermission(): boolean {
    return this.authenticationService.currentUserValue.hasPermissions(Permission.INBOX_DELETE);
  }

  get copyDeleteButtonsActive(): boolean {
    return this.agGrid && this.agGrid.api && this.agGrid.api.getSelectedRows().length > 0;
  }

  get inboxBulkButtonsActive(): boolean {
    return this.agGridInbox && this.agGridInbox.api && this.agGridInbox.api.getSelectedRows().length > 0;
  }

  public pendingInboxImportIds = new Set<string>();

  public isInboxEntryImporting(id: string): boolean {
    return this.pendingInboxImportIds.has(id);
  }

  public copyCases(): void {
    if (this.copyDeleteButtonsActive) {
      window.alert('Copy cases, not implemented yet.');
    }

    /*
    let selectedRows = this.getSelectedRows();

    let data = selectedRows.map(row => {
      return new CopyData({
        from: row.id,
        value: "copy_admin_data, copy_vehicle_data"
      });
    });

    this.caseService.copyCase(data)
      .subscribe(function(result) {
        this.getCaseOverviewData(false);
      },
        err => this.handleError(err)
      );*/
  }

  public onColumnResized(evt: ColumnResizedEvent, isInbox?: boolean) {
    // when user finishes resizing(evt.finished === true), only then we want to write to LS
    if (!evt.finished) {
      return;
    }

    if (isInbox) {
      const inboxColumnState = this.agGridInbox.api.getColumnDefs();
      this.localStorageService.set(this.localStorageService.STORAGE_KEY_INBOX_COLUMN_STATE, inboxColumnState);

      return;
    }

    const caseColumnState = this.agGrid.api.getColumnDefs();
    this.localStorageService.set(this.localStorageService.STORAGE_KEY_CASE_COLUMN_STATE, caseColumnState);
  }

  public deleteCases(): void {
    if (this.copyDeleteButtonsActive) {
      const selectedRows = this.getSelectedRows();

      const dialogRef = this.dialog.open(ConfirmModalComponent, {
        width: '250px',
        data: {
          title: marker('case_widget.dialog.delete.title'),
          text: this.translateService.instant(marker('case_widget.dialog.delete.text'), { amount: selectedRows.length })
        }
      });

      dialogRef.afterClosed()
        .subscribe((result: boolean) => {
          if (result) {

            const data = selectedRows.map((row: any) => {
              return { value: row.id, op: OP.remove };
            });

            this.caseService.massCaseDeletion(data)
              .subscribe((_result: any) => {
                let deletionResults = selectedRows.map((row: any) => {
                  let found = _result && _result.find((deletionResult: any) => {
                    return deletionResult.case_id === row.id;
                  });

                  let message = `${row.case_no}: ${found && found.deleted ? this.translateService.instant(marker('common.label.deleted')).toUpperCase() :
                                                                            this.translateService.instant(marker('common.label.error')).toUpperCase() }`;

                  return `<span style="padding-left: 30px;">${message}</span>`
                });

                let failureCount = _result.filter((row: any) => {
                  return !row.deleted;
                }).length;

                if (failureCount) {
                  let failureSummary = `${this.translateService.instant(marker('case_widget.dialog.delete.failure'), { amount: failureCount })}`;
                  deletionResults.unshift(failureSummary);
                }

                let successCount = _result.filter((row: any) => {
                  return row.deleted;
                }).length;

                if (successCount) {
                  let successSummary = `${this.translateService.instant(marker('case_widget.dialog.delete.successfully'), {amount: successCount})}`;
                  deletionResults.unshift(successSummary);
                }

                this.showSimpleInfoDialog(marker('case_widget.dialog.delete.title'), deletionResults, '33%')
                    .subscribe(() =>{
                  this.getCaseOverviewData(false);
                });
              },
                err => this.handleError(err)
              );
          }
      });
    }
  }

  public onImportCaseFromInbox(rowData: Inbox): void {
    this.pendingInboxImportIds.add(rowData.id);
    this.agGridInbox.api.redrawRows();

    this.inboxService.importCaseFromInboxEntry(rowData).subscribe(
        (response: {status: string; data: Inbox; message?: string}) => {
          if (response.status !== 'SUCCESS') {
            this.pendingInboxImportIds.delete(rowData.id);
            this.agGridInbox.api.redrawRows();
            this.showSimpleErrorSnackbar(response.message);
          }
          this.overviewRefresh$.next(true);
        },
        err => {
          this.pendingInboxImportIds.delete(rowData.id);
          this.agGridInbox.api.redrawRows();
          this.handleError(err);
        }
    );
  }

  public deleteSelectedInboxEntries(): void {
    if (!this.inboxBulkButtonsActive) { return; }
    const selectedRows: Inbox[] = this.agGridInbox.api.getSelectedRows();

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      width: '250px',
      data: {
        title: marker('case_widget.inbox.dialog.delete.title'),
        text: this.translateService.instant(marker('case_widget.inbox.dialog.delete.text'), { amount: selectedRows.length })
      }
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.inboxService.deleteInboxEntries(selectedRows)
          .subscribe(
            (response: {status: string; data: object; message?: string}) => {
              if (response.status === 'SUCCESS') {
                this.getInboxData();
              } else {
                this.showSimpleErrorSnackbar(response.message);
              }
            },
            err => this.handleError(err)
          );
      }
    });
  }

  public onRejectCaseFromInbox(rowData: Inbox): void {

    const dialogRef = this.dialog.open(TextAreaDialogComponent, {
      width: '600px',
      data: {
        title: `${this.translateService.instant(marker('case_widget.inbox.decline_order_comment_modal.title'))}`,
        confirmBtnOverride: marker('common.btn.ok'),
        closeBtnOverride: marker('common.btn.cancel'),
        label: `${this.translateService.instant(marker('case_widget.inbox.decline_order_comment_modal.label'))}`
      }
    });

    dialogRef.afterClosed()
      .subscribe((modalResult: { text: string }) => {
        if (modalResult) {
          this.inboxService.rejectCaseFromInboxEntry(rowData, modalResult.text).subscribe(
              () => this.getInboxData(),
              err => this.handleError(err)
          );
        }
      })
    ;
  }


  private get formattedInboxColumns() {
    return this.formatColumns(this.inboxService.getInboxTableColumns());
  }

  public toggleEditMode(): void {
    this.editMode = !this.editMode;
    const colSelected = this.agGrid.columnApi.getColumn('selected');
    const colDefSelected = colSelected.getColDef();
    colDefSelected.checkboxSelection = this.editMode;
    this.agGrid.api.redrawRows();
  }

  public toggleInboxEditMode(): void {
    this.inboxEditMode = !this.inboxEditMode;
    if (!this.inboxEditMode) {
      this.agGridInbox.api.deselectAll();
      this.inboxSelectAll = false;
    }
    const colSelected = this.agGridInbox.columnApi.getColumn('selected');
    const colDefSelected = colSelected.getColDef();
    colDefSelected.checkboxSelection = this.inboxEditMode;
    this.agGridInbox.api.redrawRows();
  }

  get showLessUnAvailable(): boolean {
    return this.casesResult && this.searchCriteria.limit <= this.searchCriteria.step;
  }

  public showLess(): void {
    if (!this.showLessUnAvailable) {
      this.searchCriteria.setShowLess();
      const amountToSplice = this.rowData.length - this.searchCriteria.limit;
      this.rowData.splice((this.rowData.length - amountToSplice), amountToSplice);
      this.agGrid.api.setRowData(this.rowData);
    }
  }

  get showMoreUnAvailable(): boolean {
    return this.casesResult && this.searchCriteria.limit >= this.casesResult.row_count;
  }

  public showMore(): void {
    if (!this.showMoreUnAvailable) {
      this.searchCriteria.setShowMore(this.casesResult.row_count);
      this.getCaseOverviewData(false);
    }
  }

  private initOverviewWidget(settings: WidgetSettings) {
    this.searchCriteria = new SearchParams();
    this.searchCriteria.limit = settings.linesPerPage;
    // this.searchCriteria.step = settings.linesPerPage; // TODO: in DYNAREX step is always 5
    this.searchCriteria.search = [];
    this.searchCriteria.filter = `${CasesFrom.ALL},${this.statusFilterSelected}`;
    this.searchCriteria.columns = `${settings.columns.map((c) => c.id).join(',')},${this.contentStoreRepositoryModel}`;

    this.getCaseOverviewData(true);
    this.getInboxData();
  }

  public openSettingsModal() {
    if (!this.overviewWidget) {
      return;
    }

    const dialogRef = this.dialog.open(CaseWidgetSettingsDialogComponent, {
      width: '640px',
      data: {
        widgetId: this.overviewWidget.id,
        settings: this.overviewWidget.settings 
      }
    });

    return dialogRef.afterClosed().subscribe((data: CaseWidgetSettingsDialogResult) => {
      if (data) {
        this.overviewWidget.settings = data.settings;

        this.initOverviewWidget(data.settings);
      }
    });
  }
}

